import { LegalContact, WhosMyLawyerContent, ConfigurableOption, Identity } from "@amzn/ask-legal-domain";
import { ExcelFactory } from "./file/excel-factory";
import { FileUtils } from "../utils/file-utils";
import { DateTimeFormatter } from "../utils/date-time-utils";

export namespace LegalContactFactory {
    /**
     * Interface for legal contact export row data
     */
    export interface ExportRow {
        "Legal Contact Name": string;
        "Legal Contact Alias": string;
        "Job Title": string;
        "Badge Color": string;
        "Office Location": string;
        "Manager Name": string;
        "Manager Alias": string;
        "Legal Teams": string;
        "Legal Sub-Teams": string;
        "Scopes": string;
        "Business Leaders": string;
        "Business Leader Aliases": string;
        "Cost Center ID": string;
        "Freshness Date": string;
        "Page Link"?: string;
        [key: string]: string | undefined; // For additional dynamic fields
    }

    /**
     * Export legal contacts to an Excel file
     * @param input - Configuration for export including items, content, and file prefix
     */
    export function exportToFile(input: {
        items: LegalContact.Record[];
        additionalFieldsConfig?: WhosMyLawyerContent["dataOptions"]["additionalFieldsConfig"];
        filePrefix?: string;
        getPageLink?: (contact: LegalContact.Record) => string;
    }): void {
        if (!input.items || input.items.length === 0) {
            console.warn("No legal contacts to export");
            return;
        }

        const data = transformContactsForExport(input.items, input.additionalFieldsConfig, input.getPageLink);
        const currentTime = DateTimeFormatter.dateInYyyyMmDdHhMmSs(new Date(), "_", "_", "_");
        const fileNameWithExtension = FileUtils.getFileNameWithExtension(
            `${input.filePrefix || "Legal_Contacts"}_${currentTime}`,
            FileUtils.Extension.XLSX
        );

        ExcelFactory.writeJSONtoExcel({
            data,
            fileNameWithExtension,
            sheetName: "Legal Contacts"
        });
    }

    /**
     * Transform legal contact records into flat exportable format
     * @param contacts - Array of legal contact records
     * @param additionalFieldsConfig - Additional fields configuration
     * @returns Array of flat objects suitable for Excel export
     */
    function transformContactsForExport(
        contacts: LegalContact.Record[],
        additionalFieldsConfig?: WhosMyLawyerContent["dataOptions"]["additionalFieldsConfig"],
        getPageLink?: (contact: LegalContact.Record) => string
    ): ExportRow[] {
        return contacts.map(contact => {
            const row: ExportRow = {
                "Legal Contact Name": contact.legalContactUser?.name || "",
                "Legal Contact Alias": contact.legalContactUser?.id || "",
                "Job Title": contact.legalContactJobTitle || "",
                "Badge Color": contact.legalContactBadgeColor || "",
                "Office Location": contact.legalContactOfficeLocation || "",
                "Manager Name": contact.legalContactManager?.name || "",
                "Manager Alias": contact.legalContactManager?.id || "",
                "Legal Teams": formatConfigurableOptions(contact.legalTeamOptions),
                "Legal Sub-Teams": formatConfigurableOptions(contact.legalSubTeamOptions),
                "Scopes": formatConfigurableOptions(contact.legalScopeOptions),
                "Business Leaders": formatIdentityField(contact.businessLeader, "name"),
                "Business Leader Aliases": formatIdentityField(contact.businessLeader, "id"),
                "Cost Center ID": contact.legalContactCostCenterId || "",
                "Freshness Date": DateTimeFormatter.formatDate(contact.freshnessDate),
                ...( getPageLink && { "Page Link": getPageLink(contact) }),
            };

            // Add additional fields dynamically based on container configuration
            if (contact.additionalFields && additionalFieldsConfig) {
                additionalFieldsConfig.forEach(config => {
                    const fieldValue = contact.additionalFields?.[config.fieldKey];
                    if (fieldValue !== undefined && fieldValue !== null) {
                        if (Array.isArray(fieldValue)) {
                            row[config.name] = formatConfigurableOptions(fieldValue);
                        } else {
                            row[config.name] = String(fieldValue);
                        }
                    } else {
                        row[config.name] = "";
                    }
                });
            }

            return row;
        });
    }

    /**
     * Format array of configurable options into semicolon-delimited string
     * @param options - Array of configurable options
     * @returns Formatted string or empty string if no options
     */
    function formatConfigurableOptions(options: ConfigurableOption[] | undefined): string {
        if (!options || options.length === 0) return "";
        return options
            .map(opt => opt.displayValue || opt.id)
            .filter(val => val) // Remove empty values
            .join("; ");
    }

    /**
     * Format array of identities into semicolon-delimited string
     * @param identities - Array of identity objects
     * @param field - Field to extract ("name" or "id")
     * @returns Formatted string or empty string if no identities
     */
    function formatIdentityField(identities: Identity[] | undefined, field: "name" | "id"): string {
        if (!identities || identities.length === 0) return "";
        return identities
            .map(identity => identity[field])
            .filter(value => value) // Remove empty values
            .join("; ");
    }
}
